import Modal from "@/components/controls/Modal";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { CheckboxGroup } from "@/components/ui/checkbox-group";
import SearchInput from "@/components/ui/search-input";
import InlineLoader from "@/components/ui/inline-loader";
import { useInfiniteScrollTrigger } from "@/hooks/useInfiniteScrollTrigger";
import { usePartnerContactsInfinite } from "@/pages/partners/contacts/hooks";
import { useDeals, useUpdateDealMutation } from "@/pages/deals/hooks";
import { useQueryClient } from "@tanstack/react-query";
import PartnerContactCheckbox from "@/components/layouts/AddPeopleModal/PartnerContactCheckbox";
import { filter, includes, isEmpty, map, union, without } from "lodash";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { getEnumTKey } from "@/utils/strings";
import { ComputedFields } from "@/services/api/types";
import EntitySelectField from "@/components/controls/EntitySelectField";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { PartnerContactColumn } from "@/pages/partners/contacts/types";
import toast from "react-hot-toast";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityId: string;
  partnerId?: string | null;
  onSelectPartner?: (partnerId: string) => Promise<void>;
  addedPartnerContacts?: string[] | null | undefined;
  resetPage?: () => void;
  onAddPartnerContacts?: (newContactIds: string[]) => Promise<void>;
  entityType?: EntityType;
}

const AddPartnerContactsModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  entityId,
  partnerId,
  onSelectPartner,
  addedPartnerContacts,
  resetPage,
  onAddPartnerContacts,
  entityType = EntityType.DEAL
}) => {
  const { t } = useTranslation();
  const entityName = t(
    getEnumTKey("entities", "names", EntityType, entityType)
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContacts, setSelectedContacts] = useState<string[]>(
    addedPartnerContacts || []
  );
  const [isPersistingPartner, setIsPersistingPartner] = useState(false);

  const queryClient = useQueryClient();
  const updateDeal = useUpdateDealMutation();

  // Partner contacts are scoped to the record's linked partner, mirroring how
  // company people are scoped to the deal's companyId. With no linked partner,
  // the picker cannot resolve a scope — the user selects a partner first.
  const partnerFilter = useMemo(
    () =>
      partnerId ? { [PartnerContactColumn.PARTNER]: [partnerId] } : {},
    [partnerId]
  );

  const {
    entities: partnerContacts,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError
  } = usePartnerContactsInfinite(
    partnerFilter,
    searchTerm ? { term: searchTerm } : undefined,
    undefined,
    undefined,
    "firstName",
    true,
    undefined,
    Boolean(partnerId),
    ComputedFields.MINIMAL
  );

  const {
    entities: [dealData]
  } = useDeals(onAddPartnerContacts ? [] : [entityId || ""]);

  const { rootRef, sentinelRef } = useInfiniteScrollTrigger({
    canLoadMore: !!hasNextPage && !isFetchingNextPage && !isError,
    onLoadMore: fetchNextPage,
    itemCount: partnerContacts?.length
  });

  useEffect(() => {
    if (isOpen) {
      setSelectedContacts(addedPartnerContacts || []);
    } else {
      setSearchTerm("");
    }
  }, [isOpen, addedPartnerContacts]);

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleSelectPartner = useCallback(
    async (selectedPartnerId: string | null) => {
      if (!selectedPartnerId || !onSelectPartner) {
        return;
      }
      setIsPersistingPartner(true);
      try {
        await onSelectPartner(selectedPartnerId);
      } catch {
        // The parent surfaces its own error toast before rejecting; keep the
        // modal open on the partner step so the user can retry.
      } finally {
        setIsPersistingPartner(false);
      }
    },
    [onSelectPartner]
  );

  const handleSelectedContactChange = (contactId: string, checked: boolean) => {
    setSelectedContacts((prevContacts) =>
      checked ? union(prevContacts, [contactId]) : without(prevContacts, contactId)
    );
  };

  const newSelectedContacts = filter(
    selectedContacts,
    (contactId) => !includes(addedPartnerContacts, contactId)
  );

  const handleAddPartnerContacts = async () => {
    if (onAddPartnerContacts) {
      try {
        await onAddPartnerContacts(newSelectedContacts);
        setIsOpen(false);
        setTimeout(() => {
          resetPage?.();
        }, 100);
      } catch {
        // The parent callback surfaces its own error toast before rejecting;
        // swallow here to avoid a duplicate toast and keep the modal open.
      }
      return;
    }

    updateDeal.mutate(
      {
        pathParams: { dealId: entityId },
        data: {
          name: dealData?.name,
          manuallyAddedPartnerContacts: [
            ...(dealData?.manuallyAddedPartnerContacts || []),
            ...newSelectedContacts
          ]
        }
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["deals", { pathParams: { dealsIds: entityId } }]
          });
          setIsOpen(false);
          setTimeout(() => {
            resetPage?.();
          }, 100);
        },
        onError: () => {
          toast.error(t("deals.dealView.updateError"));
        }
      }
    );
  };

  const partnerContactsResults = useMemo(
    () =>
      partnerContacts
        .filter((contact) =>
          searchTerm?.length > 2
            ? true
            : !includes(addedPartnerContacts, contact.id)
        )
        .sort(
          (first, second) =>
            Number(includes(addedPartnerContacts, second.id)) -
            Number(includes(addedPartnerContacts, first.id))
        ),
    [partnerContacts, addedPartnerContacts, searchTerm]
  );

  const needsPartnerSelection = !partnerId;

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[35rem]"
      data-test="deals-add-partner-contacts-modal"
      title={
        <div
          className="flex w-full pr-3 justify-between items-center"
          data-test="deals-add-partner-contacts-modal-header"
        >
          <Text
            as="div"
            variant="lgSubtitleH1"
            data-test="deals-add-partner-contacts-modal-title"
          >
            {needsPartnerSelection
              ? t("detailedView.peopleTab.selectPartnerTitle")
              : t("detailedView.peopleTab.addPartnerContactsToEntity", {
                  entityName: entityName.toLowerCase()
                })}
          </Text>
        </div>
      }
      content={
        needsPartnerSelection ? (
          <div className="flex flex-col w-[35rem] px-4 py-4 gap-3">
            <Text as="div" variant="body" className="text-secondary">
              {t("detailedView.peopleTab.selectPartnerHelp", {
                entityName: entityName.toLowerCase()
              })}
            </Text>
            {/* dropdown, like every other modal picker: the default variant
                caps its popover at 15vw, well under this field's width. */}
            <EntitySelectField
              fieldName="partner"
              displayType={FieldDisplayType.PARTNERS}
              value={null}
              onChange={handleSelectPartner}
              placeholder={t("common.selectPartner")}
              disabled={isPersistingPartner}
              variant="dropdown"
            />
            {isPersistingPartner && (
              <div className="w-full py-2 text-center">
                <InlineLoader label={t("loading")} />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col w-[35rem] h-[60vh]">
            <div tabIndex={0} className="sr-only" />
            <div className="sticky top-0 z-10 px-4 pt-3 pb-2 flex items-center gap-2">
              <SearchInput
                className="bg-transparent flex-1"
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
                placeholder={t("detailedView.peopleTab.partnerContactsSearch")}
              />
            </div>
            <div
              ref={rootRef}
              className="flex flex-col gap-3 px-4 flex-1 overflow-y-auto"
            >
              <CheckboxGroup fullWidth spacing={0} color="var(--cta)">
                {map(partnerContactsResults, (contact) => (
                  <PartnerContactCheckbox
                    key={contact?.id}
                    partnerContact={contact}
                    selectedContacts={selectedContacts}
                    handleSelectedContactChange={handleSelectedContactChange}
                    addedContacts={addedPartnerContacts}
                    entityName={entityName.toLowerCase()}
                  />
                ))}
              </CheckboxGroup>
              <div ref={sentinelRef} className="w-full py-4 text-center">
                {(isLoading || isFetchingNextPage) && (
                  <InlineLoader label={t("loading")} />
                )}
              </div>
            </div>
          </div>
        )
      }
      footer={
        needsPartnerSelection ? (
          <div
            className="flex justify-end gap-2"
            data-test="deals-add-partner-contacts-modal-footer"
          >
            <Button
              data-test="deals-add-partner-contacts-modal-cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
          </div>
        ) : (
          <div
            className="flex justify-end gap-2"
            data-test="deals-add-partner-contacts-modal-footer"
          >
            <Button
              data-test="deals-add-partner-contacts-modal-cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Button
              data-test="deals-add-partner-contacts-modal-submit-button"
              disabled={isEmpty(newSelectedContacts)}
              onClick={handleAddPartnerContacts}
            >
            {newSelectedContacts.length === 1
              ? t("detailedView.peopleTab.addPartnerContactButton")
              : t("detailedView.peopleTab.addPartnerContactsButton", {
                  numPeople:
                    newSelectedContacts.length === 0
                      ? ""
                      : newSelectedContacts.length
                })}
          </Button>
          </div>
        )
      }
    />
  );
};

export default AddPartnerContactsModal;
